import { Box, IconButton, Stack, TablePagination, TextField } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useBundleTranslation from 'i18n';

interface IProps {
    uid: string;
    pageSize: number;
    totalCount: number;
    pageIndex: number;
    setPageIndex: (page: number) => void;
}

export default function MITablePagination({ uid, pageSize, totalCount, pageIndex, setPageIndex }: IProps) {
    const { t } = useBundleTranslation(['main', 'components/common/grid/grid']);
    const containerRef = useRef<HTMLDivElement>(null);
    const [compact, setCompact] = useState(false);

    const checkOverflow = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        const parent = el.closest('.grid-footer-content') as HTMLElement | null;
        if (!parent) return;
        const parentWidth = parent.offsetWidth;
        const sibling = parent.querySelector(':scope > .MuiGrid-item') as HTMLElement | null;
        const siblingWidth = sibling ? sibling.scrollWidth : 0;
        const paginationFullWidth = 420;
        const gap = 16;

        if (!compact && siblingWidth + paginationFullWidth + gap > parentWidth) {
            setCompact(true);
        } else if (compact && siblingWidth + paginationFullWidth + gap <= parentWidth) {
            setCompact(false);
        }
    }, [compact]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const parent = el.closest('.grid-footer-content') as HTMLElement | null;
        if (!parent) return;

        const observer = new ResizeObserver(() => {
            checkOverflow();
        });
        observer.observe(parent);
        checkOverflow();

        return () => observer.disconnect();
    }, [checkOverflow]);

    return (
        <TablePagination
            ref={containerRef}
            data-test={`grid_${uid}_pagination`}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
            component="div"
            count={totalCount}
            getItemAriaLabel={(type) => {
                if (type === 'next') {
                    return t('pagination_go_to_next_page');
                }
                if (type === 'previous') {
                    return t('pagination_go_to_previous_page');
                }
                if (type === 'first') {
                    return t('pagination_go_to_first_page');
                }
                if (type === 'last') {
                    return t('pagination_go_to_first_page');
                }
                return '';
            }}
            page={pageIndex}
            showFirstButton
            showLastButton
            sx={{
                '.MuiTablePagination-spacer': {
                    display: 'none',
                },
                '.MuiToolbar-root': {
                    padding: 0,
                    minHeight: 'auto',
                },
                '.MuiToolbar-root .MuiTablePagination-actions .MuiIconButton-root': {
                    padding: '3px 10px',
                },
                '.MuiTablePagination-actions': {
                    display: 'flex',
                },
                '.MuiTablePagination-displayedRows': compact ? { display: 'none' } : {},
            }}
            onPageChange={(event, page) => setPageIndex(page)}
            labelDisplayedRows={({ from, to, count }) =>
                `${t('records')}: ${from}-${to} ${t('pagination_of')} ${count}`
            }
            slots={{
                actions: {
                    nextButton: (props) => (
                        <CustomNextButton
                            buttonProps={props}
                            setPageIndex={setPageIndex}
                            pageIndex={pageIndex}
                            totalCount={totalCount}
                            pageSize={pageSize}
                            t={t}
                        />
                    ),
                },
            }}
        />
    );
}

const CustomNextButton = ({
    buttonProps,
    totalCount,
    pageSize,
    pageIndex,
    setPageIndex,
    t,
}: {
    buttonProps: any;
    totalCount: number;
    pageIndex: number;
    pageSize: number;
    setPageIndex: (page: number) => void;
    t: any;
}) => {
    const [inputValue, setInputValue] = useState<string>((pageIndex + 1).toString());
    const totalPages = Math.ceil(totalCount / pageSize);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (/^\d*$/.test(value)) {
            setInputValue(value);
        }
    };

    const handleInputBlur = () => {
        const numValue = parseInt(inputValue, 10);
        if (!isNaN(numValue) && numValue >= 1 && numValue <= totalPages) {
            setPageIndex(numValue - 1);
        } else {
            setInputValue((pageIndex + 1).toString());
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleInputBlur();
        }
    };

    React.useEffect(() => {
        setInputValue((pageIndex + 1).toString());
    }, [pageIndex]);

    return (
        <Stack direction="row" alignItems={'center'} spacing={0.5}>
            <Box>{t('page')}</Box>
            <TextField
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                size="small"
                variant="outlined"
                inputProps={{
                    style: { textAlign: 'center', width: `${totalPages.toString().length}ch` },
                    min: 1,
                    max: totalPages,
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        height: '28px',
                        width: `calc(${totalPages.toString().length}ch + 10px)`,
                        fontSize: '12px',
                        lineHeight: '18px',
                        color: 'primary.main',
                    },
                    '& .MuiOutlinedInput-input': {
                        padding: '4px',
                    },
                }}
            />
            <Box>{`${t('pagination_of')} ${totalPages}`}</Box>
            <IconButton {...buttonProps} />
        </Stack>
    );
};
